import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  Users, UserCheck, UserX, Shield, ShieldCheck, Clock, 
  CheckCircle, XCircle, Mail, Phone, Building, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState('requests'); // 'requests' or 'users'
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  const fetchData = useCallback(async () => {
    try {
      const [reqRes, usersRes] = await Promise.all([
        axios.get(`${API}/auth/requests`, { withCredentials: true }),
        axios.get(`${API}/auth/users`, { withCredentials: true })
      ]);
      setRequests(reqRes.data);
      setUsers(usersRes.data);
    } catch (e) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const approveRequest = async (requestId) => {
    try {
      await axios.post(`${API}/auth/requests/${requestId}/approve`, {}, { withCredentials: true });
      toast.success('Solicitud aprobada');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const rejectRequest = async (requestId) => {
    try {
      await axios.post(`${API}/auth/requests/${requestId}/reject`, {}, { withCredentials: true });
      toast.success('Solicitud rechazada');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const suspendUser = async (userId) => {
    try {
      await axios.post(`${API}/auth/users/${userId}/suspend`, {}, { withCredentials: true });
      toast.success('Usuario suspendido');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const activateUser = async (userId) => {
    try {
      await axios.post(`${API}/auth/users/${userId}/activate`, {}, { withCredentials: true });
      toast.success('Usuario activado');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const makeAdmin = async (userId) => {
    try {
      await axios.post(`${API}/auth/users/${userId}/make-admin`, {}, { withCredentials: true });
      toast.success('Usuario promovido a admin');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const removeAdmin = async (userId) => {
    try {
      await axios.post(`${API}/auth/users/${userId}/remove-admin`, {}, { withCredentials: true });
      toast.success('Rol de admin removido');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const filteredRequests = filter === 'all' ? requests : requests.filter(r => r.status === filter);

  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-zinc-500">No tienes permisos para acceder a esta página</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-purple-500" />
            Administración
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Gestiona usuarios y solicitudes de acceso
          </p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-400 hover:text-white transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 pb-2">
        <button
          onClick={() => setTab('requests')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            tab === 'requests'
              ? 'bg-purple-600/20 text-purple-400'
              : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
          }`}
        >
          <Clock className="w-4 h-4" />
          Solicitudes
          {pendingCount > 0 && (
            <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
              {pendingCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('users')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            tab === 'users'
              ? 'bg-purple-600/20 text-purple-400'
              : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
          }`}
        >
          <Users className="w-4 h-4" />
          Usuarios ({users.length})
        </button>
      </div>

      {/* Requests Tab */}
      {tab === 'requests' && (
        <div className="space-y-4">
          {/* Filter */}
          <div className="flex gap-2">
            {['pending', 'approved', 'rejected', 'all'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  filter === f
                    ? 'bg-zinc-700 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:text-white'
                }`}
              >
                {f === 'pending' && 'Pendientes'}
                {f === 'approved' && 'Aprobadas'}
                {f === 'rejected' && 'Rechazadas'}
                {f === 'all' && 'Todas'}
              </button>
            ))}
          </div>

          {/* Requests List */}
          {filteredRequests.length === 0 ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-8 text-center">
              <Clock className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500">No hay solicitudes {filter !== 'all' && filter}</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredRequests.map(req => (
                <div
                  key={req.id}
                  className={`bg-zinc-900/50 border rounded-xl p-4 ${
                    req.status === 'pending' ? 'border-yellow-500/30' : 'border-zinc-800'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-white font-medium">{req.name}</h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          req.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          req.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                          req.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {req.status === 'pending' && 'Pendiente'}
                          {req.status === 'approved' && 'Aprobada'}
                          {req.status === 'rejected' && 'Rechazada'}
                          {req.status === 'completed' && 'Completada'}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Mail className="w-3.5 h-3.5" />
                          <span>{req.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Phone className="w-3.5 h-3.5" />
                          <span>{req.phone}</span>
                        </div>
                        {req.company && (
                          <div className="flex items-center gap-2 text-zinc-400">
                            <Building className="w-3.5 h-3.5" />
                            <span>{req.company}</span>
                          </div>
                        )}
                      </div>

                      {req.message && (
                        <p className="text-zinc-500 text-sm mt-2 italic">"{req.message}"</p>
                      )}

                      <p className="text-zinc-600 text-xs mt-2">
                        {new Date(req.created_at).toLocaleString('es-ES')}
                      </p>
                    </div>

                    {req.status === 'pending' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => approveRequest(req.id)}
                          className="p-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-lg transition-colors"
                          title="Aprobar"
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => rejectRequest(req.id)}
                          className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                          title="Rechazar"
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {tab === 'users' && (
        <div className="space-y-4">
          {users.map(u => (
            <div
              key={u.user_id}
              className={`bg-zinc-900/50 border rounded-xl p-4 ${
                u.status === 'suspended' ? 'border-red-500/30 opacity-60' : 'border-zinc-800'
              }`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                      <Users className="w-5 h-5 text-zinc-400" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-medium">{u.name}</h3>
                      {u.role === 'superadmin' && (
                        <ShieldCheck className="w-4 h-4 text-purple-400" title="Superadmin" />
                      )}
                      {u.role === 'admin' && (
                        <Shield className="w-4 h-4 text-blue-400" title="Admin" />
                      )}
                      {u.status === 'suspended' && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
                          Suspendido
                        </span>
                      )}
                    </div>
                    <p className="text-zinc-500 text-sm">{u.email}</p>
                    {u.phone && <p className="text-zinc-600 text-xs">{u.phone}</p>}
                  </div>
                </div>

                {u.user_id !== user.user_id && u.role !== 'superadmin' && (
                  <div className="flex gap-2">
                    {user.role === 'superadmin' && (
                      <>
                        {u.role === 'admin' ? (
                          <button
                            onClick={() => removeAdmin(u.user_id)}
                            className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 rounded-lg text-xs transition-colors"
                          >
                            Quitar Admin
                          </button>
                        ) : (
                          <button
                            onClick={() => makeAdmin(u.user_id)}
                            className="px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg text-xs transition-colors"
                          >
                            Hacer Admin
                          </button>
                        )}
                      </>
                    )}
                    
                    {u.status === 'active' ? (
                      <button
                        onClick={() => suspendUser(u.user_id)}
                        className="p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                        title="Suspender"
                      >
                        <UserX className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => activateUser(u.user_id)}
                        className="p-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-lg transition-colors"
                        title="Activar"
                      >
                        <UserCheck className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
